import { useState, useEffect } from 'react';
import api from '../api';
import {
  Building,
  Save,
  Plus,
  Trash2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Clock,
  HelpCircle,
  Star,
  X,
  Edit2,
  Globe,
  GripVertical,
  MapPin,
  Phone,
  Mail,
  Link,
  FileText,
} from 'lucide-react';
import toast from 'react-hot-toast';

const LANGUAGES = [
  { code: 'es', name: 'Spanish', flag: '🇪🇸', placeholder: 'español' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳', placeholder: '中文' },
  { code: 'vi', name: 'Vietnamese', flag: '🇻🇳', placeholder: 'tiếng Việt' },
];

const AMENITY_ICONS = [
  'disc', 'beer', 'cash', 'car', 'wifi', 'shield-checkmark',
  'restaurant', 'cafe', 'tv', 'musical-notes', 'game-controller',
  'gift', 'card', 'star', 'trophy', 'heart',
];

// Default casino settings
const DEFAULT_CASINO_SETTINGS = {
  casino_name: 'EDH Casino',
  casino_address: '3909 Park Dr',
  casino_city: 'El Dorado Hills',
  casino_state: 'CA',
  casino_zip: '95762',
  casino_phone: '(530) 618-8946',
  casino_email: 'contact@edhcasino.com',
  casino_website: 'https://edhcasino.com',
  casino_about: "EDH Casino is El Dorado Hills' premier poker destination, featuring exciting cardroom action with a variety of games.",
  casino_latitude: '38.6857',
  casino_longitude: '-121.0760',
  social_facebook: 'https://facebook.com/edhcasino',
  social_instagram: 'https://instagram.com/edhcasino',
  social_twitter: 'https://x.com/edhcasino',
  social_tiktok: 'https://tiktok.com/@edhcasino',
  casino_about_es: '',
  casino_about_zh: '',
  casino_about_vi: '',
};

// Translation fields component
function TranslationFields({ translations, onChange, fields, expanded, onToggle }) {
  return (
    <div className="mt-4 border-t border-gray-600 pt-4">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 text-amber-500 hover:text-amber-400 mb-3"
      >
        <Globe size={16} />
        <span className="text-sm font-medium">
          {expanded ? 'Hide Translations' : 'Add Translations (Optional)'}
        </span>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      
      {expanded && (
        <div className="space-y-4 bg-gray-900/50 rounded-lg p-4">
          {LANGUAGES.map((lang) => (
            <div key={lang.code} className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-300">
                <span>{lang.flag}</span>
                <span>{lang.name}</span>
              </div>
              {fields.map((field) => (
                <input
                  key={`${lang.code}-${field.key}`}
                  type="text"
                  value={translations?.[lang.code]?.[field.key] || ''}
                  onChange={(e) => {
                    const newTranslations = { ...translations };
                    if (!newTranslations[lang.code]) newTranslations[lang.code] = {};
                    newTranslations[lang.code][field.key] = e.target.value;
                    onChange(newTranslations);
                  }}
                  placeholder={`${field.label} (${lang.placeholder})`}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CasinoInfo() {
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Data state
  const [faqs, setFaqs] = useState([]);
  const [hours, setHours] = useState({ regular: [], special: [] });
  const [amenities, setAmenities] = useState([]);
  const [settings, setSettings] = useState({});
  const [settingsModified, setSettingsModified] = useState({});

  // Modal state
  const [faqModal, setFaqModal] = useState(false);
  const [amenityModal, setAmenityModal] = useState(false);
  const [specialHoursModal, setSpecialHoursModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showTranslations, setShowTranslations] = useState(false);
  const [showAboutTranslations, setShowAboutTranslations] = useState(false);

  // Form state
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', translations: {} });
  const [amenityForm, setAmenityForm] = useState({ name: '', description: '', icon: 'star', translations: {} });
  const [specialForm, setSpecialForm] = useState({ date: '', name: '', open: '10:00 AM', close: '3:00 AM', closed: false });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [faqsRes, hoursRes, amenitiesRes, settingsRes] = await Promise.all([
        api.get('/casino-info/admin/faqs'),
        api.get('/casino-info/admin/hours'),
        api.get('/casino-info/admin/amenities'),
        api.get('/casino-info/admin/settings'),
      ]);
      console.log('Hours response:', hoursRes.data);
      setFaqs(faqsRes.data.faqs || []);
      setHours(hoursRes.data.hours || { regular: [], special: [] });
      setAmenities(amenitiesRes.data.amenities || []);
      
      // Extract casino settings and merge with defaults
      const casinoSettings = { ...DEFAULT_CASINO_SETTINGS, ...settingsRes.data.settings };
      
      console.log('Loaded casino settings:', casinoSettings);
      setSettings(casinoSettings);
    } catch (err) {
      toast.error('Failed to load casino info');
      console.error(err);
      // Set defaults on error
      setSettings({ ...DEFAULT_CASINO_SETTINGS });
      setHours({ regular: [], special: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // FAQ Functions
  const openFaqModal = (faq = null) => {
    if (faq) {
      setEditingItem(faq);
      setFaqForm({
        question: faq.question,
        answer: faq.answer,
        translations: faq.translations || {},
      });
    } else {
      setEditingItem(null);
      setFaqForm({ question: '', answer: '', translations: {} });
    }
    setShowTranslations(false);
    setFaqModal(true);
  };

  const saveFaq = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim()) {
      toast.error('Question and answer are required');
      return;
    }
    
    // Clean up empty translations
    const cleanTranslations = {};
    Object.entries(faqForm.translations).forEach(([lang, fields]) => {
      const cleanFields = {};
      Object.entries(fields).forEach(([key, value]) => {
        if (value?.trim()) cleanFields[key] = value.trim();
      });
      if (Object.keys(cleanFields).length > 0) cleanTranslations[lang] = cleanFields;
    });

    try {
      setSaving(true);
      let updatedFaqs;
      
      if (editingItem) {
        updatedFaqs = faqs.map(f => f.id === editingItem.id ? {
          ...f,
          question: faqForm.question,
          answer: faqForm.answer,
          translations: Object.keys(cleanTranslations).length > 0 ? cleanTranslations : undefined,
        } : f);
      } else {
        const newFaq = {
          id: `faq-${Date.now()}`,
          question: faqForm.question,
          answer: faqForm.answer,
          order: faqs.length + 1,
          isActive: true,
          translations: Object.keys(cleanTranslations).length > 0 ? cleanTranslations : undefined,
        };
        updatedFaqs = [...faqs, newFaq];
      }
      
      await api.put('/casino-info/admin/faqs', { faqs: updatedFaqs });
      setFaqs(updatedFaqs);
      setFaqModal(false);
      toast.success(editingItem ? 'FAQ updated' : 'FAQ created');
    } catch (err) {
      toast.error('Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const deleteFaq = async (id) => {
    if (!confirm('Delete this FAQ?')) return;
    try {
      const updatedFaqs = faqs.filter(f => f.id !== id);
      await api.put('/casino-info/admin/faqs', { faqs: updatedFaqs });
      setFaqs(updatedFaqs);
      toast.success('FAQ deleted');
    } catch (err) {
      toast.error('Failed to delete FAQ');
    }
  };

  const toggleFaqActive = async (faq) => {
    try {
      const updatedFaqs = faqs.map(f => f.id === faq.id ? { ...f, isActive: !f.isActive } : f);
      await api.put('/casino-info/admin/faqs', { faqs: updatedFaqs });
      setFaqs(updatedFaqs);
      toast.success(`FAQ ${faq.isActive ? 'hidden' : 'shown'}`);
    } catch (err) {
      toast.error('Failed to update FAQ');
    }
  };

  // Hours Functions
  const updateRegularHours = (day, field, value) => {
    const updated = hours.regular.map(h => h.day === day ? { ...h, [field]: value } : h);
    setHours({ ...hours, regular: updated });
  };

  const saveHours = async () => {
    try {
      setSaving(true);
      await api.put('/casino-info/admin/hours', { regular: hours.regular, special: hours.special });
      toast.success('Hours saved');
    } catch (err) {
      toast.error('Failed to save hours');
    } finally {
      setSaving(false);
    }
  };

  const openSpecialHoursModal = () => {
    setSpecialForm({ date: '', name: '', open: '10:00 AM', close: '3:00 AM', closed: false });
    setSpecialHoursModal(true);
  };

  const saveSpecialHours = async () => {
    if (!specialForm.date || !specialForm.name) {
      toast.error('Date and name are required');
      return;
    }
    try {
      setSaving(true);
      await api.post('/casino-info/admin/hours/special', {
        date: specialForm.date,
        name: specialForm.name,
        open: specialForm.closed ? null : specialForm.open,
        close: specialForm.closed ? null : specialForm.close,
        closed: specialForm.closed,
      });
      await fetchData();
      setSpecialHoursModal(false);
      toast.success('Special hours added');
    } catch (err) {
      toast.error('Failed to add special hours');
    } finally {
      setSaving(false);
    }
  };

  const deleteSpecialHours = async (id) => {
    if (!confirm('Delete these special hours?')) return;
    try {
      await api.delete(`/casino-info/admin/hours/special/${id}`);
      setHours({ ...hours, special: hours.special.filter(s => s.id !== id) });
      toast.success('Special hours deleted');
    } catch (err) {
      toast.error('Failed to delete special hours');
    }
  };

  // Amenity Functions
  const openAmenityModal = (amenity = null) => {
    if (amenity) {
      setEditingItem(amenity);
      setAmenityForm({
        name: amenity.name,
        description: amenity.description,
        icon: amenity.icon,
        translations: amenity.translations || {},
      });
    } else {
      setEditingItem(null);
      setAmenityForm({ name: '', description: '', icon: 'star', translations: {} });
    }
    setShowTranslations(false);
    setAmenityModal(true);
  };

  const saveAmenity = async () => {
    if (!amenityForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    // Clean up empty translations
    const cleanTranslations = {};
    Object.entries(amenityForm.translations).forEach(([lang, fields]) => {
      const cleanFields = {};
      Object.entries(fields).forEach(([key, value]) => {
        if (value?.trim()) cleanFields[key] = value.trim();
      });
      if (Object.keys(cleanFields).length > 0) cleanTranslations[lang] = cleanFields;
    });

    try {
      setSaving(true);
      let updatedAmenities;
      
      if (editingItem) {
        updatedAmenities = amenities.map(a => a.id === editingItem.id ? {
          ...a,
          name: amenityForm.name,
          description: amenityForm.description,
          icon: amenityForm.icon,
          translations: Object.keys(cleanTranslations).length > 0 ? cleanTranslations : undefined,
        } : a);
      } else {
        const newAmenity = {
          id: `amenity-${Date.now()}`,
          name: amenityForm.name,
          description: amenityForm.description,
          icon: amenityForm.icon,
          order: amenities.length + 1,
          isActive: true,
          translations: Object.keys(cleanTranslations).length > 0 ? cleanTranslations : undefined,
        };
        updatedAmenities = [...amenities, newAmenity];
      }
      
      await api.put('/casino-info/admin/amenities', { amenities: updatedAmenities });
      setAmenities(updatedAmenities);
      setAmenityModal(false);
      toast.success(editingItem ? 'Amenity updated' : 'Amenity created');
    } catch (err) {
      toast.error('Failed to save amenity');
    } finally {
      setSaving(false);
    }
  };

  const deleteAmenity = async (id) => {
    if (!confirm('Delete this amenity?')) return;
    try {
      const updatedAmenities = amenities.filter(a => a.id !== id);
      await api.put('/casino-info/admin/amenities', { amenities: updatedAmenities });
      setAmenities(updatedAmenities);
      toast.success('Amenity deleted');
    } catch (err) {
      toast.error('Failed to delete amenity');
    }
  };

  // Check if item has translations
  const hasTranslations = (item) => {
    return item.translations && Object.keys(item.translations).length > 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building className="text-amber-500" size={28} />
          <h1 className="text-2xl font-bold text-white">Casino Information</h1>
        </div>
        <button onClick={fetchData} className="p-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600">
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-800 p-1 rounded-lg w-fit">
        {[
          { key: 'details', label: 'Details', icon: FileText },
          { key: 'faqs', label: 'FAQs', icon: HelpCircle },
          { key: 'hours', label: 'Hours', icon: Clock },
          { key: 'amenities', label: 'Amenities', icon: Star },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === key
                ? 'bg-amber-500 text-black'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
      </div>

      {/* Details Tab */}
      {activeTab === 'details' && (
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Building size={20} className="text-amber-500" />
              Basic Information
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Casino Name</label>
                <input
                  type="text"
                  value={settingsModified.casino_name ?? settings.casino_name ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, casino_name: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Phone</label>
                <input
                  type="text"
                  value={settingsModified.casino_phone ?? settings.casino_phone ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, casino_phone: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  value={settingsModified.casino_email ?? settings.casino_email ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, casino_email: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Website</label>
                <input
                  type="url"
                  value={settingsModified.casino_website ?? settings.casino_website ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, casino_website: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <MapPin size={20} className="text-amber-500" />
              Location
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-1">Street Address</label>
                <input
                  type="text"
                  value={settingsModified.casino_address ?? settings.casino_address ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, casino_address: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">City</label>
                <input
                  type="text"
                  value={settingsModified.casino_city ?? settings.casino_city ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, casino_city: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">State</label>
                  <input
                    type="text"
                    value={settingsModified.casino_state ?? settings.casino_state ?? ''}
                    onChange={(e) => setSettingsModified({ ...settingsModified, casino_state: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">ZIP</label>
                  <input
                    type="text"
                    value={settingsModified.casino_zip ?? settings.casino_zip ?? ''}
                    onChange={(e) => setSettingsModified({ ...settingsModified, casino_zip: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Latitude</label>
                <input
                  type="text"
                  value={settingsModified.casino_latitude ?? settings.casino_latitude ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, casino_latitude: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Longitude</label>
                <input
                  type="text"
                  value={settingsModified.casino_longitude ?? settings.casino_longitude ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, casino_longitude: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
            </div>
          </div>

          {/* About Section with Translations */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <FileText size={20} className="text-amber-500" />
              About Text
            </h3>
            <div>
              <label className="block text-sm text-gray-400 mb-1">About (English)</label>
              <textarea
                rows={4}
                value={settingsModified.casino_about ?? settings.casino_about ?? ''}
                onChange={(e) => setSettingsModified({ ...settingsModified, casino_about: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white resize-none"
                placeholder="Describe your casino..."
              />
            </div>
            
            {/* About Translations */}
            <div className="mt-4 border-t border-gray-600 pt-4">
              <button
                type="button"
                onClick={() => setShowAboutTranslations(!showAboutTranslations)}
                className="flex items-center gap-2 text-amber-500 hover:text-amber-400"
              >
                <Globe size={16} />
                <span className="text-sm font-medium">
                  {showAboutTranslations ? 'Hide Translations' : 'Add Translations (Optional)'}
                </span>
                {showAboutTranslations ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              
              {showAboutTranslations && (
                <div className="mt-4 space-y-4 bg-gray-900/50 rounded-lg p-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">🇪🇸 Spanish</label>
                    <textarea
                      rows={3}
                      value={settingsModified.casino_about_es ?? settings.casino_about_es ?? ''}
                      onChange={(e) => setSettingsModified({ ...settingsModified, casino_about_es: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white resize-none"
                      placeholder="Descripción en español..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">🇨🇳 Chinese</label>
                    <textarea
                      rows={3}
                      value={settingsModified.casino_about_zh ?? settings.casino_about_zh ?? ''}
                      onChange={(e) => setSettingsModified({ ...settingsModified, casino_about_zh: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white resize-none"
                      placeholder="中文描述..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">🇻🇳 Vietnamese</label>
                    <textarea
                      rows={3}
                      value={settingsModified.casino_about_vi ?? settings.casino_about_vi ?? ''}
                      onChange={(e) => setSettingsModified({ ...settingsModified, casino_about_vi: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white resize-none"
                      placeholder="Mô tả tiếng Việt..."
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Social Media */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Link size={20} className="text-amber-500" />
              Social Media
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Facebook URL</label>
                <input
                  type="url"
                  value={settingsModified.social_facebook ?? settings.social_facebook ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, social_facebook: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Instagram URL</label>
                <input
                  type="url"
                  value={settingsModified.social_instagram ?? settings.social_instagram ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, social_instagram: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">X/Twitter URL</label>
                <input
                  type="url"
                  value={settingsModified.social_twitter ?? settings.social_twitter ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, social_twitter: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">TikTok URL</label>
                <input
                  type="url"
                  value={settingsModified.social_tiktok ?? settings.social_tiktok ?? ''}
                  onChange={(e) => setSettingsModified({ ...settingsModified, social_tiktok: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          {Object.keys(settingsModified).length > 0 && (
            <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <span className="text-amber-400">
                {Object.keys(settingsModified).length} unsaved changes
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSettingsModified({})}
                  className="px-4 py-2 text-gray-400 hover:text-white"
                >
                  Discard
                </button>
                <button
                  onClick={async () => {
                    try {
                      setSaving(true);
                      const updates = Object.entries(settingsModified).map(([key, value]) => ({ key, value }));
                      await api.put('/admin/settings', { settings: updates });
                      setSettings({ ...settings, ...settingsModified });
                      setSettingsModified({});
                      toast.success('Settings saved');
                    } catch (err) {
                      toast.error('Failed to save settings');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400 disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* FAQs Tab */}
      {activeTab === 'faqs' && (
        <div className="space-y-4">
          <button
            onClick={() => openFaqModal()}
            className="flex items-center gap-2 px-4 py-3 bg-gray-800 border-2 border-dashed border-amber-500/50 text-amber-500 rounded-lg hover:bg-gray-700 hover:border-amber-500 w-full justify-center"
          >
            <Plus size={20} />
            Add New FAQ
          </button>

          {faqs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No FAQs yet</div>
          ) : (
            <div className="space-y-3">
              {faqs.map((faq) => (
                <div
                  key={faq.id}
                  className={`bg-gray-800 rounded-lg p-4 ${!faq.isActive ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-medium">{faq.question}</h3>
                        {hasTranslations(faq) && (
                          <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                            Translated
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm line-clamp-2">{faq.answer}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleFaqActive(faq)}
                        className={`px-3 py-1 text-xs rounded ${
                          faq.isActive
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-gray-600 text-gray-400'
                        }`}
                      >
                        {faq.isActive ? 'Active' : 'Hidden'}
                      </button>
                      <button
                        onClick={() => openFaqModal(faq)}
                        className="p-2 text-gray-400 hover:text-amber-500 hover:bg-gray-700 rounded"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => deleteFaq(faq.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-700 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hours Tab */}
      {activeTab === 'hours' && (
        <div className="space-y-6">
          {/* Regular Hours */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="text-white font-semibold mb-4">Regular Hours</h3>
            <div className="space-y-3">
              {(hours.regular || []).map((h) => (
                <div key={h.day} className="flex items-center gap-4">
                  <span className="text-white w-28">{h.day}</span>
                  <input
                    type="text"
                    value={h.open}
                    onChange={(e) => updateRegularHours(h.day, 'open', e.target.value)}
                    className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm w-28"
                    placeholder="Open"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="text"
                    value={h.close}
                    onChange={(e) => updateRegularHours(h.day, 'close', e.target.value)}
                    className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm w-28"
                    placeholder="Close"
                  />
                </div>
              ))}
            </div>
            <button
              onClick={saveHours}
              disabled={saving}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400"
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Hours'}
            </button>
          </div>

          {/* Special Hours */}
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Special Hours (Holidays, Events)</h3>
              <button
                onClick={openSpecialHoursModal}
                className="flex items-center gap-1 text-amber-500 hover:text-amber-400"
              >
                <Plus size={18} />
                Add
              </button>
            </div>
            {(hours.special || []).length === 0 ? (
              <p className="text-gray-500 text-center py-4">No special hours scheduled</p>
            ) : (
              <div className="space-y-2">
                {(hours.special || []).map((s) => (
                  <div key={s.id} className="flex items-center justify-between bg-gray-700/50 rounded-lg p-3">
                    <div>
                      <p className="text-white font-medium">{s.name}</p>
                      <p className="text-gray-400 text-sm">{s.date}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={s.closed ? 'text-red-400' : 'text-amber-400'}>
                        {s.closed ? 'Closed' : `${s.open} - ${s.close}`}
                      </span>
                      <button
                        onClick={() => deleteSpecialHours(s.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Amenities Tab */}
      {activeTab === 'amenities' && (
        <div className="space-y-4">
          <button
            onClick={() => openAmenityModal()}
            className="flex items-center gap-2 px-4 py-3 bg-gray-800 border-2 border-dashed border-amber-500/50 text-amber-500 rounded-lg hover:bg-gray-700 hover:border-amber-500 w-full justify-center"
          >
            <Plus size={20} />
            Add New Amenity
          </button>

          {amenities.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No amenities yet</div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {amenities.map((amenity) => (
                <div key={amenity.id} className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center text-amber-500">
                      <span className="text-lg">{amenity.icon === 'disc' ? '🎰' : amenity.icon === 'beer' ? '🍺' : amenity.icon === 'cash' ? '💵' : amenity.icon === 'car' ? '🚗' : amenity.icon === 'wifi' ? '📶' : '⭐'}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-medium">{amenity.name}</h3>
                        {hasTranslations(amenity) && (
                          <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                            Translated
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm">{amenity.description}</p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openAmenityModal(amenity)}
                        className="p-2 text-gray-400 hover:text-amber-500 hover:bg-gray-700 rounded"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => deleteAmenity(amenity.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-700 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAQ Modal */}
      {faqModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 p-4 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingItem ? 'Edit FAQ' : 'New FAQ'}
              </h2>
              <button onClick={() => setFaqModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Question (English)</label>
                <input
                  type="text"
                  value={faqForm.question}
                  onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  placeholder="Enter question..."
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Answer (English)</label>
                <textarea
                  value={faqForm.answer}
                  onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                  rows={4}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white resize-none"
                  placeholder="Enter answer..."
                />
              </div>

              <TranslationFields
                translations={faqForm.translations}
                onChange={(t) => setFaqForm({ ...faqForm, translations: t })}
                fields={[
                  { key: 'question', label: 'Question' },
                  { key: 'answer', label: 'Answer' },
                ]}
                expanded={showTranslations}
                onToggle={() => setShowTranslations(!showTranslations)}
              />

              <button
                onClick={saveFaq}
                disabled={saving}
                className="w-full py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save FAQ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Amenity Modal */}
      {amenityModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 p-4 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingItem ? 'Edit Amenity' : 'New Amenity'}
              </h2>
              <button onClick={() => setAmenityModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Icon</label>
                <div className="flex flex-wrap gap-2">
                  {['🎰', '🍺', '💵', '🚗', '📶', '🛡️', '🍽️', '☕', '📺', '🎵', '🎮', '🎁', '💳', '⭐', '🏆', '❤️'].map((emoji, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setAmenityForm({ ...amenityForm, icon: AMENITY_ICONS[i] })}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${
                        amenityForm.icon === AMENITY_ICONS[i]
                          ? 'bg-amber-500 ring-2 ring-amber-400'
                          : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name (English)</label>
                <input
                  type="text"
                  value={amenityForm.name}
                  onChange={(e) => setAmenityForm({ ...amenityForm, name: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  placeholder="Amenity name"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description (English)</label>
                <input
                  type="text"
                  value={amenityForm.description}
                  onChange={(e) => setAmenityForm({ ...amenityForm, description: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  placeholder="Short description"
                />
              </div>

              <TranslationFields
                translations={amenityForm.translations}
                onChange={(t) => setAmenityForm({ ...amenityForm, translations: t })}
                fields={[
                  { key: 'name', label: 'Name' },
                  { key: 'description', label: 'Description' },
                ]}
                expanded={showTranslations}
                onToggle={() => setShowTranslations(!showTranslations)}
              />

              <button
                onClick={saveAmenity}
                disabled={saving}
                className="w-full py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Amenity'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Special Hours Modal */}
      {specialHoursModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Add Special Hours</h2>
              <button onClick={() => setSpecialHoursModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Date</label>
                <input
                  type="date"
                  value={specialForm.date}
                  onChange={(e) => setSpecialForm({ ...specialForm, date: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={specialForm.name}
                  onChange={(e) => setSpecialForm({ ...specialForm, name: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  placeholder="e.g. Christmas Day"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="closed"
                  checked={specialForm.closed}
                  onChange={(e) => setSpecialForm({ ...specialForm, closed: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="closed" className="text-white">Closed all day</label>
              </div>
              {!specialForm.closed && (
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={specialForm.open}
                    onChange={(e) => setSpecialForm({ ...specialForm, open: e.target.value })}
                    className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    placeholder="Open"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="text"
                    value={specialForm.close}
                    onChange={(e) => setSpecialForm({ ...specialForm, close: e.target.value })}
                    className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    placeholder="Close"
                  />
                </div>
              )}
              <button
                onClick={saveSpecialHours}
                disabled={saving}
                className="w-full py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Add Special Hours'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
